import type { ComponentPropsWithoutRef } from "react";

import { useDispatch } from "react-redux";

import { Type } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import { setDataCreditsModalOpen } from "app/pages/sales-intelligence/modules/quota/store";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { openHooksModalV4 } from "app/services/ModalService";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import type LeadText from "../LeadText/LeadText";
import LinkOut from "../LinkOut/LinkOut";

import { StyledCombinedIcon, StyledIconTextWrapper, StyledLeadText } from "./styles";

type CompanyNameCellProps = Omit<ComponentPropsWithoutRef<typeof LeadText>, "tooltipContent"> & {
    iconSrc: string;
    websiteLink?: string;
    width?: string;
    onClick?: () => void;
    isSidebarABTest?: boolean;
};

const CompanyNameCell = ({
    children,
    overviewLink,
    iconSrc,
    websiteLink,
    onClick,
    width = "100%",
    isSidebarABTest = false,
}: CompanyNameCellProps) => {
    const dispatch = useDispatch();
    const { isQuotaReached } = useCompanyQuota();
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;
    const isNTUser = isNoTouch(swSettings);

    const handleLockedLinkClick = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
        event.preventDefault();
        if (isDataCreditsEnabled && isNTUser) {
            dispatch(setDataCreditsModalOpen(true));
        } else {
            openHooksModalV4("SalesSolutionQueryLimiterSIRedirect");
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
        if (isQuotaReached && !isSidebarABTest) {
            handleLockedLinkClick(event);
        } else if (onClick) {
            onClick();
        }
    };

    const tooltipContent = isSidebarABTest
        ? translate("si.table.cell.LeadCell.tooltip.lead-name.sidebar.text")
        : translate("si.table.cell.LeadCell.tooltip.lead-name.text");

    return (
        <StyledIconTextWrapper width={width}>
            <StyledCombinedIcon
                type="itemIcon"
                iconType={Type.Website}
                iconSrc={iconSrc}
            />
            <StyledLeadText
                overviewLink={overviewLink}
                tooltipContent={tooltipContent}
                onClick={handleClick}
                dataAutomation={`lead-text-ab-test-${isSidebarABTest}`}
            >
                {children}
            </StyledLeadText>
            {websiteLink ? (
                <LinkOut
                    tooltipContent={translate(`table.cell.CompanyCell.tooltip.website-link.text`)}
                    href={websiteLink}
                />
            ) : null}
        </StyledIconTextWrapper>
    );
};

export default CompanyNameCell;
